"use client";

import { Handle, type NodeProps, Position } from "@xyflow/react";
import { AlertTriangle, Database, Layers, User } from "lucide-react";
import { memo, useState } from "react";
import { useDirection } from "../DirectionContext";
import type { SystemNodeData } from "../types";
import { AdminNodeActions } from "./shared/AdminNodeActions";
import { ElapsedTime } from "./shared/ElapsedTime";
import { NodeFrame } from "./shared/NodeFrame";
import { ProgressBar } from "./shared/ProgressBar";
import {
  calcBarPercent,
  formatProgressMetrics,
} from "./shared/progress-metrics";
import { StatusBadge } from "./shared/StatusBadge";
import { STATUS_COLORS } from "./shared/status-styles";

const ICONS: Record<string, typeof Database> = {
  "Fetch Data": Database,
  "Contact Compaction": Layers,
  "Customer Profile": User,
};

export const SystemNode = memo(function SystemNode({ id, data }: NodeProps) {
  const d = data as SystemNodeData;
  const dir = useDirection();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const Icon = ICONS[d.label] ?? Database;
  const barPct = d.contentLength ? calcBarPercent(d.contentLength) : 0;
  const colors = STATUS_COLORS[d.status];
  const isFailed = d.status === "failed";
  const isActive = d.status === "active";
  const isReceiving =
    (d.tokensReceived ?? 0) > 0 ||
    (d.bytesReceived ?? 0) > 0 ||
    (d.contentLength ?? 0) > 0;

  let stateClass = "";
  if (d.status === "pending") {
    stateClass = "!bg-amber-50 !border-l-amber-200";
  } else if (isActive && isReceiving) {
    stateClass =
      "pipeline-node-active-streaming !bg-blue-50 !border-l-blue-400";
  } else if (isActive) {
    stateClass =
      "pipeline-node-active-waiting !bg-amber-100 !border-l-amber-400";
  }

  return (
    <NodeFrame
      status={d.status}
      className={`min-w-[136px] w-max max-w-[210px] ${stateClass}`}
    >
      <Handle
        type="target"
        position={dir === "LR" ? Position.Left : Position.Top}
        className="!bg-transparent !border-0 !w-0 !h-0"
      />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <StatusBadge status={d.status} />
          <Icon size={14} className={`shrink-0 ${colors.icon}`} />
          <span
            className={`nodrag cursor-text select-text text-xs font-medium ${colors.text}`}
          >
            {d.label}
          </span>
        </div>
        <ElapsedTime ms={d.elapsedMs} />
      </div>
      <div className="mt-1.5">
        <ProgressBar percent={barPct} status={d.status} />
        <div
          className={`nodrag cursor-text select-text text-[10px] mt-0.5 tabular-nums ${d.status === "completed" ? "text-emerald-500" : "text-gray-400"}`}
        >
          {formatProgressMetrics(
            d.tokensReceived,
            d.contentLength,
            d.bytesReceived,
          )}
        </div>
      </div>
      <AdminNodeActions
        nodeId={d.nodeId ?? id}
        nodeStatus={d.status}
        nodeExecutionDirectives={d.nodeExecutionDirectives}
        onAdminAction={d.onAdminAction}
      />
      {isFailed && d.error && (
        <div className="mt-1 flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setShowErrorDialog(true)}
            className="text-[10px] px-1.5 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors inline-flex items-center gap-1"
          >
            <AlertTriangle size={10} />
            Details
          </button>
        </div>
      )}
      {showErrorDialog && d.error && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="nodrag bg-white rounded-lg shadow-xl border border-red-200 w-[min(520px,90vw)] max-h-[70vh] overflow-auto">
            <div className="px-4 py-2.5 border-b border-red-100 flex items-center justify-between">
              <div className="nodrag cursor-text select-text text-sm font-semibold text-red-700">
                System Stage Error Details
              </div>
              <button
                type="button"
                onClick={() => setShowErrorDialog(false)}
                className="nodrag text-xs text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <div className="p-3">
              <div className="rounded border border-red-200 bg-red-50 p-2">
                <div className="nodrag cursor-text select-text text-xs font-medium text-red-700">
                  {d.label}
                </div>
                <div className="nodrag cursor-text select-text mt-1 text-xs text-red-700 whitespace-pre-wrap break-words">
                  {d.error}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <Handle
        type="source"
        position={dir === "LR" ? Position.Right : Position.Bottom}
        className="!bg-transparent !border-0 !w-0 !h-0"
      />
    </NodeFrame>
  );
});
